import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import HandshakeIcon from '@mui/icons-material/Handshake';
import BusinessIcon from '@mui/icons-material/Business';
import { Props } from '../../../Components/Layout/DashBoard/Model/HealthModels';
import { healthService } from '../../../Components/Layout/DashBoard/Services/HealthService';
import { customerService } from '../../../Components/Layout/Customer/CustomerService';
import GlobalHealth from '../../../Components/Layout/DashBoard/Components/GlobalHealth';
import TenantHealthPage from '../../../Components/Layout/DashBoard/Components/TenantHealthPage';
import { Tenant } from '../../../Components/Layout/Farm/Model/FarmModel';
import { CustomerModule } from '../../../Components/Layout/Customer/Model/CustomerModel';

interface TenantStats {
  active: number;
  collaboration: number;
  standard: number;
}

const initalStats: TenantStats = { active: 0, collaboration: 0, standard: 0 };

const HealthHome: React.FC<Props> = ({ product }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [stats, setStats] = useState<TenantStats>(initalStats);

  const loadTenantStats = useCallback(async () => {
    try {
      const [tenantsRes, modulesRes] = await Promise.all([
        healthService.GetActiveTenants(product),
        customerService.getAllCustomerModules(),
      ]);

      const tenantIds = new Set(tenantsRes.Items.map((t: Tenant) => t.Id));
      const collabIds = new Set(
        modulesRes.Items.filter(
          (m: CustomerModule) => m.ModuleID === 'collaboration',
        ).map((m: CustomerModule) => m.CustomerID),
      );

      const collaboration = tenantsRes.Items.filter((t: Tenant) =>
        collabIds.has(t.Id),
      ).length;

      setStats({
        active: tenantIds.size,
        collaboration,
        standard: tenantIds.size - collaboration,
      });
    } catch (err) {
      console.error('Failed to load tenant stats:', err);
    }
  }, [product]);

  useEffect(() => {
    loadTenantStats();
  }, [loadTenantStats]);

  const statCards = [
    {
      title: 'Total Active Tenants',
      value: stats.active.toString(),
      icon: PeopleIcon,
      color: 'secondary' as const,
    },
    {
      title: 'Collaboration',
      value: stats.collaboration.toString(),
      icon: HandshakeIcon,
      color: 'info' as const,
    },
    {
      title: 'Standard',
      value: stats.standard.toString(),
      icon: BusinessIcon,
      color: 'success' as const,
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: 'background.default',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, md: 3 },
        gap: 3,
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}
        >
          Service Health Dashboard
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600 }}
        >
          Real-time operational status for {product}
        </Typography>
      </Box>

      {/* STATS RIBBON */}
      <Paper
        elevation={1}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          bgcolor: 'background.paper',
          borderRadius: 3,
        }}
      >
        {statCards.map(({ title, value, icon: Icon, color }, index) => (
          <React.Fragment key={title}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? `${theme.palette[color].main}22`
                      : `${theme.palette[color].main}15`,
                  color: `${theme.palette[color].main}`,
                }}
              >
                <Icon />
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ lineHeight: 1, display: 'block', mb: 0.5 }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ lineHeight: 1 }}
                >
                  {value}
                </Typography>
              </Box>
            </Box>
            {index < statCards.length - 1 && (
              <Divider
                orientation={isMobile ? 'horizontal' : 'vertical'}
                flexItem
              />
            )}
          </React.Fragment>
        ))}
      </Paper>

      <GlobalHealth product={product} />

      <Box sx={{ flexGrow: 1 }}>
        <TenantHealthPage product={product} />
      </Box>
    </Box>
  );
};

export default HealthHome;
